/**
 * Context-aware toolbar for DocumentEditor.
 *
 * Renders formatting buttons grouped by category (inline marks, headings,
 * block elements, history). Buttons are disabled based on which section
 * type the cursor is currently in:
 *
 * - atomicField: ALL buttons disabled (interactive React widget only)
 * - sectionTitle: inline marks only (no block operations)
 * - paragraphSection: inline marks only (no headings, lists, etc.)
 * - richSection: full formatting (headings, lists, code blocks, blockquote)
 */

import type { Id } from "@repo/convex/dataModel";
import type { Editor } from "@tiptap/react";
import {
  Bold,
  Code,
  Database,
  Heading2,
  Heading3,
  Highlighter,
  ImagePlus,
  Italic,
  List,
  ListOrdered,
  MessageSquarePlus,
  PencilSparkles,
  Quote,
  Redo,
  RefreshCw,
  SquareCode,
  Strikethrough,
  Undo,
} from "lucide-react";
import { memo } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { HIGHLIGHT_COLORS, type HighlightColor } from "../../extensions/highlight-mark";
import { type UseVoiceComposer, useVoiceComposer } from "../../hooks/use-voice-composer";
import { ComposerRecordingStrip } from "../composer-recording-strip";
import { TranscribingIndicator } from "../transcribing-indicator";
import { VoiceTriggerButton } from "../voice-trigger-button";
import { type DocumentMenuState, useDocumentMenuState } from "./document-menu-state";

// ─── Menu Option Definition ─────────────────────────────────────────────────

interface MenuOption {
  key: string;
  label: string;
  icon: React.ReactNode;
  pressed: boolean;
  disabled: boolean;
  onClick: () => void;
}

type ClaudiaEditorAction = "proofread" | "shorten" | "convert-to-bullets" | "convert-to-paragraph";

const highlightColorLabels: Record<HighlightColor, string> = {
  yellow: "Yellow",
  green: "Green",
  red: "Red",
};

const highlightSwatchClasses: Record<HighlightColor, string> = {
  yellow: "bg-(--editor-highlight-yellow)",
  green: "bg-(--editor-highlight-green)",
  red: "bg-(--editor-highlight-red)",
};

const codeLanguages = [
  { value: "plaintext", label: "Plain text" },
  { value: "javascript", label: "JavaScript" },
  { value: "typescript", label: "TypeScript" },
  { value: "tsx", label: "TSX" },
  { value: "json", label: "JSON" },
  { value: "sql", label: "SQL" },
  { value: "bash", label: "Bash" },
  { value: "python", label: "Python" },
];

// ─── Option Groups ───────────────────────────────────────────────────────────

function createMenuGroups(editor: Editor, s: DocumentMenuState): MenuOption[][] {
  // Disabling logic based on section context:
  // - atomicField → all disabled (it's a React widget, no text editing)
  // - sectionTitle → inline only (no blocks at all)
  // - paragraphSection → inline only (paragraphs only, no headings/lists)
  // - richSection → everything enabled
  const allDisabled = s.isAtomicField;
  const blockOpsDisabled = allDisabled || s.isSectionTitle || s.isParagraphSection;

  const inlineMarks: MenuOption[] = [
    {
      key: "bold",
      label: "Bold",
      icon: <Bold className="size-4" />,
      pressed: s.isBold,
      disabled: allDisabled || !s.canBold,
      onClick: () => editor.chain().focus().toggleBold().run(),
    },
    {
      key: "italic",
      label: "Italic",
      icon: <Italic className="size-4" />,
      pressed: s.isItalic,
      disabled: allDisabled || !s.canItalic,
      onClick: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      key: "strike",
      label: "Strikethrough",
      icon: <Strikethrough className="size-4" />,
      pressed: s.isStrike,
      disabled: allDisabled || !s.canStrike,
      onClick: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      key: "code",
      label: "Inline code",
      icon: <Code className="size-4" />,
      pressed: s.isCode,
      disabled: allDisabled || !s.canCode,
      onClick: () => editor.chain().focus().toggleCode().run(),
    },
  ];

  const headings: MenuOption[] = [
    {
      key: "h2",
      label: "Heading 2",
      icon: <Heading2 className="size-4" />,
      pressed: s.isHeading2,
      disabled: blockOpsDisabled,
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      key: "h3",
      label: "Heading 3",
      icon: <Heading3 className="size-4" />,
      pressed: s.isHeading3,
      disabled: blockOpsDisabled,
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
  ];

  const blockElements: MenuOption[] = [
    {
      key: "bulletList",
      label: "Bullet list",
      icon: <List className="size-4" />,
      pressed: s.isBulletList,
      disabled: blockOpsDisabled,
      onClick: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      key: "orderedList",
      label: "Ordered list",
      icon: <ListOrdered className="size-4" />,
      pressed: s.isOrderedList,
      disabled: blockOpsDisabled,
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      key: "codeBlock",
      label: "Code block",
      icon: <SquareCode className="size-4" />,
      pressed: s.isCodeBlock,
      disabled: blockOpsDisabled || !s.canCodeBlock,
      onClick: () => {
        const chain = editor.chain().focus() as unknown as Record<string, unknown>;
        const toggleCodeBlock = chain.toggleCodeBlock;
        if (typeof toggleCodeBlock !== "function") return;
        (toggleCodeBlock as () => { run: () => boolean }).call(chain).run();
      },
    },
    {
      key: "blockquote",
      label: "Blockquote",
      icon: <Quote className="size-4" />,
      pressed: s.isBlockquote,
      disabled: blockOpsDisabled,
      onClick: () => editor.chain().focus().toggleBlockquote().run(),
    },
  ];

  const history: MenuOption[] = [
    {
      key: "undo",
      label: "Undo",
      icon: <Undo className="size-4" />,
      pressed: false,
      disabled: !s.canUndo,
      onClick: () => editor.chain().focus().undo().run(),
    },
    {
      key: "redo",
      label: "Redo",
      icon: <Redo className="size-4" />,
      pressed: false,
      disabled: !s.canRedo,
      onClick: () => editor.chain().focus().redo().run(),
    },
  ];

  return [inlineMarks, headings, blockElements, history];
}

// ─── Component ───────────────────────────────────────────────────────────────

interface DocumentMenuBarProps {
  editor: Editor | null;
  /** When true, the entire toolbar is visually disabled and non-interactive. */
  disabled?: boolean;
  onInsertImage?: () => void;
  onInsertDataCatalog?: () => void;
  onRefreshLinkedInformation?: () => void;
  onRequestClarification?: (anchorRect: DOMRect | null) => void;
  imageDisabled?: boolean;
  imageUploading?: boolean;
  dataCatalogDisabled?: boolean;
  dataCatalogTitle?: string;
  refreshLinkedInformationDisabled?: boolean;
  refreshLinkedInformationTitle?: string;
  clarificationDisabled?: boolean;
  onClaudiaAction?: (action: ClaudiaEditorAction) => void;
  claudiaDisabled?: boolean;
  runningClaudiaAction?: string | null;
  voiceInputProjectId?: Id<"projects"> | null;
}

interface DocumentFormattingToolbarContentsProps {
  editor: Editor;
  groups: MenuOption[][];
  menuState: DocumentMenuState;
  imageContextDisabled: boolean;
  imageDisabled: boolean;
  imageUploading: boolean;
  onInsertImage?: () => void;
  onInsertDataCatalog?: () => void;
  onRefreshLinkedInformation?: () => void;
  dataCatalogDisabled?: boolean;
  dataCatalogTitle?: string;
  refreshLinkedInformationDisabled?: boolean;
  refreshLinkedInformationTitle?: string;
  onRequestClarification?: (anchorRect: DOMRect | null) => void;
  clarificationDisabled: boolean;
  onClaudiaAction?: (action: ClaudiaEditorAction) => void;
  claudiaDisabled: boolean;
  runningClaudiaAction: string | null;
  voiceInputProjectId: Id<"projects"> | null;
  voice: Pick<UseVoiceComposer, "start" | "unavailable" | "isTranscribing">;
}

function DocumentToolbarOptionButton({ option }: { option: MenuOption }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<span />}>
        <Button
          variant="ghost"
          size="icon-sm"
          data-active={option.pressed}
          className="text-muted-foreground data-[active=true]:bg-accent data-[active=true]:text-accent-foreground"
          onClick={option.onClick}
          disabled={option.disabled}
          aria-label={option.label}
          aria-pressed={option.pressed}
        >
          {option.icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{option.label}</TooltipContent>
    </Tooltip>
  );
}

function DocumentHighlightMenu({
  editor,
  menuState,
}: {
  editor: Editor;
  menuState: DocumentMenuState;
}) {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger render={<span />}>
          <DropdownMenuTrigger
            data-active={menuState.isHighlight}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[active=true]:bg-accent data-[active=true]:text-accent-foreground"
            disabled={menuState.isAtomicField || !menuState.canHighlight}
            aria-label="Highlight"
          >
            <Highlighter className="size-4" />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">Highlight</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" className="w-44">
        {HIGHLIGHT_COLORS.map((color) => (
          <DropdownMenuItem
            key={color}
            data-active={menuState.highlightColor === color}
            className="data-[active=true]:bg-accent data-[active=true]:text-accent-foreground"
            // Set, never toggle: picking the color a selection already has must keep the
            // highlight (toggling would clear it), and "Remove highlight" below is the only
            // way to unset.
            onClick={() => editor.chain().focus().setHighlight({ color }).run()}
          >
            <span
              aria-hidden="true"
              className={cn("size-3.5 border border-neutral-border", highlightSwatchClasses[color])}
            />
            {highlightColorLabels[color]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!menuState.isHighlight}
          onClick={() => editor.chain().focus().unsetHighlight().run()}
        >
          Remove highlight
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface DocumentImageButtonProps {
  disabled: boolean;
  uploading: boolean;
  onClick?: () => void;
}

function DocumentImageButton({ disabled, uploading, onClick }: DocumentImageButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger render={<span />}>
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={onClick}
          disabled={disabled}
          aria-label="Insert image"
        >
          {uploading ? <Spinner className="size-4" /> : <ImagePlus className="size-4" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {uploading ? "Uploading image..." : "Insert image"}
      </TooltipContent>
    </Tooltip>
  );
}

function DocumentFormattingToolbarContents({
  editor,
  groups,
  menuState,
  imageContextDisabled,
  imageDisabled,
  imageUploading,
  onInsertImage,
  onInsertDataCatalog,
  onRefreshLinkedInformation,
  onRequestClarification,
  dataCatalogDisabled = false,
  dataCatalogTitle = "Browse data catalog",
  refreshLinkedInformationDisabled = false,
  refreshLinkedInformationTitle = "Refresh linked information",
  clarificationDisabled,
  onClaudiaAction,
  claudiaDisabled,
  runningClaudiaAction,
  voiceInputProjectId,
  voice,
}: DocumentFormattingToolbarContentsProps) {
  const formattingGroups = groups.slice(0, -1);
  const historyGroup = groups[groups.length - 1] ?? [];

  return (
    <>
      {formattingGroups.map((group, groupIndex) => (
        <div key={group[0]?.key} className="flex items-center gap-0.5">
          {groupIndex > 0 && (
            <Separator orientation="vertical" className="mx-1 h-[18px] bg-border" />
          )}
          {group.map((option) => (
            <DocumentToolbarOptionButton key={option.key} option={option} />
          ))}
          {/* Highlight is an inline mark, so it rides along with the first group. */}
          {groupIndex === 0 ? (
            <DocumentHighlightMenu editor={editor} menuState={menuState} />
          ) : null}
        </div>
      ))}
      <Separator orientation="vertical" className="mx-1 h-[18px] bg-border" />
      <DocumentImageButton
        disabled={imageDisabled || imageContextDisabled}
        uploading={imageUploading}
        onClick={onInsertImage}
      />
      {onInsertDataCatalog ? (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onInsertDataCatalog}
          disabled={dataCatalogDisabled || imageContextDisabled}
          aria-label="Browse data catalog"
          title={dataCatalogTitle}
        >
          <Database className="size-4" />
        </Button>
      ) : null}
      {onRefreshLinkedInformation ? (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onRefreshLinkedInformation}
          disabled={refreshLinkedInformationDisabled || imageContextDisabled}
          aria-label="Refresh linked information"
          title={refreshLinkedInformationTitle}
        >
          <RefreshCw className="size-4" />
        </Button>
      ) : null}
      {historyGroup.length > 0 ? (
        <>
          <Separator orientation="vertical" className="mx-1 h-[18px] bg-border" />
          <div key={historyGroup[0]?.key} className="flex items-center gap-0.5">
            {historyGroup.map((option) => (
              <DocumentToolbarOptionButton key={option.key} option={option} />
            ))}
          </div>
        </>
      ) : null}
      <Separator orientation="vertical" className="mx-1 h-[18px] bg-border" />
      <Tooltip>
        <TooltipTrigger render={<span />}>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground"
            onClick={(event) =>
              onRequestClarification?.(
                (event.currentTarget as HTMLButtonElement).getBoundingClientRect()
              )
            }
            disabled={clarificationDisabled || !onRequestClarification}
            aria-label="Request clarification"
          >
            <MessageSquarePlus className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Request clarification</TooltipContent>
      </Tooltip>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger render={<span />}>
            <DropdownMenuTrigger
              className={cn(
                "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
              )}
              disabled={claudiaDisabled || !onClaudiaAction}
              aria-label="Claudia editor actions"
            >
              {runningClaudiaAction ? (
                <Spinner className="size-4" />
              ) : (
                // Deliberately not Claudia's `Sparkles` mark: this menu only rewrites the
                // selected text, and reusing the assistant's icon led users to expect the
                // full assistant (LAT-803).
                <PencilSparkles className="size-4" />
              )}
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Claudia editor actions</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuItem onClick={() => onClaudiaAction?.("proofread")}>
            Proofread
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClaudiaAction?.("shorten")}>Shorten</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClaudiaAction?.("convert-to-bullets")}>
            Convert to bullets
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClaudiaAction?.("convert-to-paragraph")}>
            Convert to paragraph
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {menuState.isCodeBlock ? (
        <>
          <Separator orientation="vertical" className="mx-1 h-[18px] bg-border" />
          <Select
            value={menuState.codeBlockLanguage}
            onValueChange={(value) =>
              editor.chain().focus().updateAttributes("codeBlock", { language: value }).run()
            }
          >
            <SelectTrigger className="h-8 w-36 text-xs" aria-label="Code block language">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              {codeLanguages.map((language) => (
                <SelectItem key={language.value} value={language.value}>
                  {language.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      ) : null}
      {voiceInputProjectId ? (
        <div className="ml-auto flex items-center">
          <VoiceTriggerButton voice={voice} />
        </div>
      ) : null}
    </>
  );
}

function DocumentMenuBarComponent({
  editor,
  disabled = false,
  onInsertImage,
  onInsertDataCatalog,
  onRefreshLinkedInformation,
  onRequestClarification,
  imageDisabled = false,
  imageUploading = false,
  dataCatalogDisabled = false,
  dataCatalogTitle = "Browse data catalog",
  refreshLinkedInformationDisabled = false,
  refreshLinkedInformationTitle = "Refresh linked information",
  clarificationDisabled = false,
  onClaudiaAction,
  claudiaDisabled = false,
  runningClaudiaAction = null,
  voiceInputProjectId = null,
}: DocumentMenuBarProps) {
  const menuState = useDocumentMenuState(editor);
  // Lifted here (not inside VoiceTriggerButton) so the whole toolbar can swap to the recording
  // strip while recording. Called before the early return to respect rules-of-hooks; the hook
  // no-ops when editor/projectId is null. Same options the old toolbar button used: snapshot the
  // cursor on start and lock the editor so the insert position can't move.
  const voice = useVoiceComposer({
    editor,
    projectId: voiceInputProjectId,
    source: "document-editor",
    surface: "entity_page",
    disabled: disabled || menuState.isAtomicField,
    snapshotSelectionOnStart: true,
    lockEditorWhileRecording: true,
  });

  if (!editor) return null;

  const groups = createMenuGroups(editor, menuState);
  const imageContextDisabled =
    menuState.isAtomicField || menuState.isSectionTitle || menuState.isParagraphSection;

  return (
    <div
      className={cn(
        "flex min-h-9 items-center gap-0.5 border-b border-border px-1.5 py-1",
        // Keep the strip/bar visible if the toolbar is disabled mid-recording or while
        // transcribing (✕/✓ must stay live; the progress bar must not dim).
        disabled && !voice.isRecording && !voice.isTranscribing && "pointer-events-none opacity-35"
      )}
      role="toolbar"
      aria-label={
        voice.isRecording
          ? "Voice recording"
          : voice.isTranscribing
            ? "Transcribing voice input"
            : "Formatting"
      }
      aria-disabled={disabled && !voice.isRecording && !voice.isTranscribing}
    >
      {voice.isRecording ? (
        <ComposerRecordingStrip voice={voice} showAddContextPlaceholder={false} />
      ) : voice.isTranscribing ? (
        <TranscribingIndicator showLeadingPlaceholder={false} />
      ) : (
        <DocumentFormattingToolbarContents
          editor={editor}
          groups={groups}
          menuState={menuState}
          imageContextDisabled={imageContextDisabled}
          imageDisabled={imageDisabled}
          imageUploading={imageUploading}
          onInsertImage={onInsertImage}
          onInsertDataCatalog={onInsertDataCatalog}
          onRefreshLinkedInformation={onRefreshLinkedInformation}
          dataCatalogDisabled={dataCatalogDisabled}
          dataCatalogTitle={dataCatalogTitle}
          refreshLinkedInformationDisabled={refreshLinkedInformationDisabled}
          refreshLinkedInformationTitle={refreshLinkedInformationTitle}
          onRequestClarification={onRequestClarification}
          clarificationDisabled={clarificationDisabled}
          onClaudiaAction={onClaudiaAction}
          claudiaDisabled={claudiaDisabled}
          runningClaudiaAction={runningClaudiaAction}
          voiceInputProjectId={voiceInputProjectId}
          voice={voice}
        />
      )}
    </div>
  );
}

export const DocumentMenuBar = memo(DocumentMenuBarComponent);
